import React, { useRef, useEffect, useState } from 'react'
import { Chart as ChartJS, registerables } from 'chart.js'

// Register Chart.js components once globally with safety check for test environment
if (registerables && Array.isArray(registerables)) {
  ChartJS.register(...registerables)
}

/**
 * Common hook for Chart.js setup and ref management
 */
export const useChart = <T = ChartJS>() => {
  const chartRef = useRef<T>(null)

  // Clean up chart instance on unmount
  useEffect(() => () => {
    if (chartRef.current && typeof (chartRef.current as any).destroy === 'function') {
      (chartRef.current as any).destroy()
    }
  }, [])

  return chartRef
}

/**
 * Hook for processing chart data with error handling
 */
export const useChartData = <TData, TProcessed>(
  rawData: TData | undefined,
  processor: (data: TData) => TProcessed,
  dependencies: any[] = [],
  onError?: (error: Error) => void
): {
  processedData: TProcessed | null
  isProcessing: boolean
} => {
  const [processedData, setProcessedData] = useState<TProcessed | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  useEffect(() => {
    if (!rawData) {
      setProcessedData(null)
      return
    }

    setIsProcessing(true)
    
    try {
      const result = processor(rawData)
      setProcessedData(result)
    } catch (error) {
      console.error('Error processing chart data:', error)
      if (onError) {
        onError(error instanceof Error ? error : new Error('Unknown processing error'))
      }
      setProcessedData(null)
    } finally {
      setIsProcessing(false)
    }
  }, [rawData, processor, onError, ...dependencies])

  return { processedData, isProcessing }
}